import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';

export function LiveVoicePanel({ language, turns, onExchange, onClose }: { language: string; turns: { role: string; content: string }[]; onExchange: (user: string, assistant: string) => void; onClose: () => void }) {
  const [state, setState] = useState('connecting');
  const [error, setError] = useState('');
  const [userText, setUserText] = useState('');
  const [assistantText, setAssistantText] = useState('');
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        if (!window.RTCPeerConnection || !navigator.mediaDevices?.getUserMedia) throw new Error('Live voice is not supported by this browser.');
        const pc = new RTCPeerConnection(); pcRef.current = pc;
        const audio = new Audio(); audio.autoplay = true; audioRef.current = audio;
        pc.ontrack = (event) => { audio.srcObject = event.streams[0]; audio.play().catch(() => {}); };
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
        streamRef.current = stream; stream.getTracks().forEach((track) => pc.addTrack(track, stream));
        const dc = pc.createDataChannel('oai-events'); dcRef.current = dc;
        dc.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            if (message.type === 'conversation.item.input_audio_transcription.completed') setUserText(String(message.transcript || ''));
            if (message.type === 'response.output_audio_transcript.delta') { setAssistantText((prev) => prev + String(message.delta || '')); setState('speaking'); }
            if (message.type === 'output_audio_buffer.stopped') setState('listening');
            if (message.type === 'error') setError(message.error?.message || 'Realtime voice failed.');
          } catch (_) {}
        };
        dc.onopen = () => { setState('listening'); const context = turns.slice(-8).map((turn) => `${turn.role}: ${turn.content.slice(0, 800)}`).join('\n'); if (context) dc.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text: `Conversation context:\n${context}` }] } })); };
        const offer = await pc.createOffer(); await pc.setLocalDescription(offer);
        // Every other OZIRA request goes through apiClient, which attaches
        // this same bearer token -- but apiClient always JSON-encodes its
        // body, and this endpoint's body is a raw WebRTC SDP offer
        // (content-type: application/sdp), so the token has to be attached
        // by hand here instead. POST /api/ai/realtime 401s without it.
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        const response = await fetch(`/api/ai/realtime?ground=1&lang=${encodeURIComponent(language)}`, {
          method: 'POST',
          headers: { 'content-type': 'application/sdp', ...(token ? { authorization: `Bearer ${token}` } : {}) },
          body: offer.sdp,
        });
        const answer = await response.text();
        if (!response.ok) {
          let message = answer;
          try { message = JSON.parse(answer)?.error || answer; } catch (_) {}
          throw new Error(message || 'Realtime connection failed.');
        }
        await pc.setRemoteDescription({ type: 'answer', sdp: answer });
      } catch (err) { if (!cancelled) { setError(err instanceof Error ? err.message : 'Realtime voice failed.'); setState('error'); } }
    };
    start();
    return () => { cancelled = true; streamRef.current?.getTracks().forEach((track) => track.stop()); pcRef.current?.close(); };
  }, [language, turns]);

  const close = () => { if (userText.trim() && assistantText.trim()) onExchange(userText.trim(), assistantText.trim()); onClose(); };
  const statusLabel = state === 'listening' ? 'Listening…' : state === 'speaking' ? 'OZIRA is speaking…' : state === 'connecting' ? 'Connecting…' : 'Could not connect.';
  return <div className="ozira-cw-modal-backdrop" role="presentation"><div className="ozira-cw-panel" role="dialog" aria-modal="true" aria-label="Live voice"><h3>Live voice</h3><p role="status">{statusLabel}</p>{assistantText ? <p>{assistantText}</p> : null}{error ? <small>{error}</small> : null}<div className="ozira-cw-modal-actions"><button type="button" className="ozira-button" onClick={close}>End live voice</button></div></div></div>;
}
